import React from "react";
import { motion } from "framer-motion";
import { UserCheck, Target, BookOpen, FileCheck, Award } from "lucide-react";

const steps = [
  {
    number: "01",
    title: "Fit & Readiness Assessment",
    description:
      "We begin by understanding your child's strengths, aspirations, and family priorities to determine true readiness.",
    icon: UserCheck,
  },
  {
    number: "02",
    title: "School Strategy & Shortlist",
    description:
      "Based on fit criteria, we curate a strategic shortlist of schools that align with your child's profile and goals.",
    icon: Target,
  },
  {
    number: "03",
    title: "Testing & Interview Preparation",
    description:
      "Comprehensive preparation for UKiset, ISEB, CAT4 and school-specific assessments, plus interview coaching.",
    icon: BookOpen,
  },
  {
    number: "04",
    title: "Application Execution & Deadlines",
    description:
      "We manage every detail — personal statements, references, forms, and submissions — ensuring nothing is missed.",
    icon: FileCheck,
  },
  {
    number: "05",
    title: "Offer, Negotiation & Enrolment",
    description:
      "From comparing offers to scholarship negotiation and acceptance, we support you through to day one.",
    icon: Award,
  },
];

const Method = () => {
  return (
    <section id="method" className="py-20 md:py-28 bg-[#FDFCF8]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-4">
            Our Approach
          </p>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-medium text-[#1A202C] tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            The ESA Method:{" "}
            <span className="text-[#2B4C7E]">Five Steps to Success</span>
          </h2>
          <p className="text-lg text-[#4A5568] leading-relaxed">
            A structured, personalised framework that guides families from initial
            exploration to successful enrolment.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="max-w-3xl mx-auto relative">
          {/* Vertical Line */}
          <div className="hidden md:block absolute left-[27px] top-8 bottom-8 w-0.5 bg-gradient-to-b from-[#C9A227] via-[#C9A227] to-[#E2E8F0]" />

          <div className="space-y-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="flex gap-6 md:gap-8"
                  data-testid={`method-step-${index}`}
                >
                  {/* Number Circle */}
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-[#C9A227] flex items-center justify-center shadow-lg">
                      <span
                        className="text-white text-lg font-semibold"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* Content Card */}
                  <div className="flex-1 bg-white p-6 md:p-8 rounded-[4px] border border-[#E2E8F0] hover:border-[#C9A227] hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <Icon className="w-6 h-6 text-[#2B4C7E] flex-shrink-0 mt-1" />
                      <div>
                        <h3
                          className="text-xl md:text-2xl font-medium text-[#1A202C] mb-2"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {step.title}
                        </h3>
                        <p className="text-[#4A5568] leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Method;
